import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@trovata/environments/environment';
import { StablecoinPage } from '../../models/stablecoin.model';
import { StablecoinTransfer } from '../../models/transfer.model';
import {
	StablecoinActivityFilter,
	StablecoinActivitySummaryRow,
	StablecoinBalancesReportRow,
	StablecoinGroupBy,
	StablecoinReportDefinition,
	StablecoinReportDefinitionCreate,
} from '../../models/report.model';

/**
 * Low-level HTTP service for stablecoin reporting and saved report definitions.
 * State/components should use StablecoinReportsFacadeService instead.
 */
@Injectable({
	providedIn: 'root',
})
export class StablecoinReportsService {
	private readonly http: HttpClient = inject(HttpClient);
	private readonly baseUrl: string = environment.stablecoinAPI();

	private filterParams(filter: StablecoinActivityFilter): HttpParams {
		let params: HttpParams = new HttpParams();
		Object.entries(filter).forEach(([key, value]: [string, string | null | undefined]) => {
			if (value !== null && value !== undefined && value !== '') {
				params = params.set(key, value);
			}
		});
		return params;
	}

	getActivity(filter: StablecoinActivityFilter, cursor?: string): Observable<StablecoinPage<StablecoinTransfer>> {
		let params: HttpParams = this.filterParams(filter);
		if (cursor) {
			params = params.set('page_cursor', cursor);
		}
		return this.http.get<StablecoinPage<StablecoinTransfer>>(`${this.baseUrl}/reports/activity`, { params });
	}

	getActivitySummary(groupBy: StablecoinGroupBy, filter: StablecoinActivityFilter): Observable<StablecoinActivitySummaryRow[]> {
		const params: HttpParams = this.filterParams(filter).set('group_by', groupBy);
		return this.http.get<StablecoinActivitySummaryRow[]>(`${this.baseUrl}/reports/activity/summary`, { params });
	}

	getBalancesReport(): Observable<StablecoinBalancesReportRow[]> {
		return this.http.get<StablecoinBalancesReportRow[]>(`${this.baseUrl}/reports/balances`);
	}

	exportActivity(filter: StablecoinActivityFilter): Observable<HttpResponse<Blob>> {
		const params: HttpParams = this.filterParams(filter);
		return this.http.get(`${this.baseUrl}/reports/activity/export`, {
			params,
			responseType: 'blob',
			observe: 'response',
		});
	}

	getReportDefinitions(): Observable<StablecoinReportDefinition[]> {
		return this.http.get<StablecoinReportDefinition[]>(`${this.baseUrl}/report-definitions`);
	}

	createReportDefinition(body: StablecoinReportDefinitionCreate): Observable<StablecoinReportDefinition> {
		return this.http.post<StablecoinReportDefinition>(`${this.baseUrl}/report-definitions`, body);
	}

	updateReportDefinition(reportId: string, body: StablecoinReportDefinitionCreate): Observable<StablecoinReportDefinition> {
		return this.http.put<StablecoinReportDefinition>(`${this.baseUrl}/report-definitions/${reportId}`, body);
	}

	deleteReportDefinition(reportId: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/report-definitions/${reportId}`);
	}
}
